import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ArrowLeft,
  Package,
  Plus,
  Trash2,
  X,
  Calendar,
  Truck,
  Scale,
  Search,
  FileText,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Loader2,
  Download
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RawMaterials() {
  const { user, loading: authLoading } = useAuth();
  const { companyId, selectedCompany } = useCompany();
  const navigate = useNavigate();
  
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deletingInvoice, setDeletingInvoice] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grouped'); // 'grouped' or 'list'
  const [expandedInvoices, setExpandedInvoices] = useState(new Set());
  
  // Filter states
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all'); // all, 7, 30, 90
  
  // Check if user has edit/delete permission
  const canEdit = user?.effective_permissions?.includes('edit_raw_materials') || false;
  
  // Form state
  const [formData, setFormData] = useState({
    material_name: '',
    quantity: '',
    unit: 'kg',
    supplier: '',
    batch_number: '',
    received_date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  const fetchMaterials = useCallback(async () => {
    if (!companyId) return;
    
    try {
      const response = await fetch(`${API_URL}/api/raw-materials?company_id=${companyId}&limit=100`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setMaterials(data.materials || []);
      }
    } catch (error) {
      console.error('Failed to fetch raw materials:', error);
      toast.error('Failed to load raw materials');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (!authLoading && user && companyId) {
      fetchMaterials();
    }
  }, [authLoading, user, companyId, fetchMaterials]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.material_name.trim() || !formData.quantity || !formData.received_date) {
      toast.error('Please fill in required fields');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/raw-materials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          company_id: companyId,
          quantity: parseFloat(formData.quantity)
        })
      });

      if (response.ok) {
        toast.success('Raw material entry added');
        setShowAddModal(false);
        setFormData({
          material_name: '',
          quantity: '',
          unit: 'kg',
          supplier: '',
          batch_number: '',
          received_date: new Date().toISOString().split('T')[0],
          notes: ''
        });
        fetchMaterials();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add entry');
      }
    } catch (error) {
      toast.error('Failed to add entry');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (materialId) => {
    setDeletingId(materialId);
    try {
      const response = await fetch(`${API_URL}/api/raw-materials/${materialId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Entry deleted');
        fetchMaterials();
      } else {
        toast.error('Failed to delete entry');
      }
    } catch (error) {
      toast.error('Failed to delete entry');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteInvoice = async (invoiceNumber) => {
    setDeletingInvoice(invoiceNumber);
    try {
      // Different endpoint for "No Invoice" vs regular invoices
      // Use query params instead of path params to handle slashes in invoice numbers
      const url = invoiceNumber === 'No Invoice'
        ? `${API_URL}/api/raw-materials/no-invoice?company_id=${companyId}`
        : `${API_URL}/api/raw-materials/by-invoice?invoice_number=${encodeURIComponent(invoiceNumber)}&company_id=${companyId}`;
      
      const response = await fetch(url, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchMaterials();
        setConfirmDelete(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete');
      }
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setDeletingInvoice(null);
    }
  };

  const toggleInvoice = (invoiceNum) => {
    const newExpanded = new Set(expandedInvoices);
    if (newExpanded.has(invoiceNum)) {
      newExpanded.delete(invoiceNum);
    } else {
      newExpanded.add(invoiceNum);
    }
    setExpandedInvoices(newExpanded);
  };

  const handleDownloadInvoice = async (invoiceNumber) => {
    try {
      const url = invoiceNumber && invoiceNumber !== 'No Invoice'
        ? `${API_URL}/api/raw-materials/export?company_id=${companyId}&invoice_number=${encodeURIComponent(invoiceNumber)}`
        : `${API_URL}/api/raw-materials/export?company_id=${companyId}`;
      
      const response = await fetch(url, { credentials: 'include' });
      
      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `raw_materials_${invoiceNumber || 'all'}_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
        toast.success('Download started');
      } else {
        toast.error('Failed to download');
      }
    } catch (error) {
      toast.error('Download failed');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Get unique suppliers for filter dropdown
  const uniqueSuppliers = [...new Set(materials.map(m => m.supplier).filter(Boolean))].sort();

  // Filter materials by search, supplier, and date
  const filteredMaterials = materials.filter(m => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch = m.material_name?.toLowerCase().includes(query) ||
             m.supplier?.toLowerCase().includes(query) ||
             m.batch_number?.toLowerCase().includes(query);
      if (!matchesSearch) return false;
    }
    
    // Supplier filter
    if (supplierFilter !== 'all' && m.supplier !== supplierFilter) {
      return false;
    }
    
    // Date filter
    if (dateFilter !== 'all' && m.received_date) {
      const days = parseInt(dateFilter);
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - days);
      const itemDate = new Date(m.received_date);
      if (itemDate < cutoffDate) return false;
    }
    
    return true;
  });

  // Group materials by batch/invoice number
  const groupedMaterials = filteredMaterials.reduce((acc, mat) => {
    const key = mat.batch_number || 'No Invoice';
    if (!acc[key]) {
      acc[key] = {
        batch_number: mat.batch_number,
        supplier: mat.supplier,
        received_date: mat.received_date,
        items: []
      };
    }
    // Update the group's received_date to the most recent item's date
    if (mat.received_date && (!acc[key].received_date || mat.received_date > acc[key].received_date)) {
      acc[key].received_date = mat.received_date;
    }
    acc[key].items.push(mat);
    return acc;
  }, {});

  // Sort grouped materials by date (most recent first)
  const sortedGroupedMaterials = Object.entries(groupedMaterials).sort(([, a], [, b]) => {
    const dateA = a.received_date ? new Date(a.received_date) : new Date(0);
    const dateB = b.received_date ? new Date(b.received_date) : new Date(0);
    return dateB - dateA; // Most recent first
  });

  const uniqueInvoices = [...new Set(materials.map(m => m.batch_number).filter(Boolean))];

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="loading-state">
          <div className="loading-spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-content" data-testid="raw-materials-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="h-10 w-10 p-0"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Raw Materials</h1>
              <p className="text-slate-500 mt-1">Track incoming raw materials - {selectedCompany?.name}</p>
            </div>
          </div>
          {canEdit && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => handleDownloadInvoice(null)}
                data-testid="download-all-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export All
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/invoices')}
                data-testid="upload-invoice-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                Upload Invoice
              </Button>
              <Button
                onClick={() => setShowAddModal(true)}
                className="bg-green-600 hover:bg-green-700"
                data-testid="add-material-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Entry
              </Button>
            </div>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{materials.length}</p>
                <p className="text-sm text-slate-500">Total Entries</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {materials.filter(m => m.received_date === new Date().toISOString().split('T')[0]).length}
                </p>
                <p className="text-sm text-slate-500">Today's Entries</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Truck className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {[...new Set(materials.map(m => m.supplier?.trim()?.toLowerCase()).filter(s => s && s.length > 0))].length}
                </p>
                <p className="text-sm text-slate-500">Suppliers</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white border border-slate-200 rounded-lg p-4 mb-6">
          <div className="flex flex-col gap-3">
            {/* Top row: Search and View toggle */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search materials, suppliers, or batch numbers..."
                  className="pl-10 h-11"
                  data-testid="search-input"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  variant={viewMode === 'grouped' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('grouped')}
                  className="h-11"
                >
                  Grouped
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className="h-11"
                >
                  List
                </Button>
              </div>
            </div>
            
            {/* Bottom row: Filters */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Truck className="w-4 h-4 text-slate-400" />
                <Select value={supplierFilter} onValueChange={setSupplierFilter}>
                  <SelectTrigger className="w-48 h-9" data-testid="supplier-filter">
                    <SelectValue placeholder="All Suppliers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Suppliers</SelectItem>
                    {uniqueSuppliers.map(supplier => (
                      <SelectItem key={supplier} value={supplier}>{supplier}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-400" />
                <Select value={dateFilter} onValueChange={setDateFilter}>
                  <SelectTrigger className="w-36 h-9" data-testid="date-filter">
                    <SelectValue placeholder="All Time" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Time</SelectItem>
                    <SelectItem value="7">Last 7 Days</SelectItem>
                    <SelectItem value="30">Last 30 Days</SelectItem>
                    <SelectItem value="90">Last 90 Days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {(supplierFilter !== 'all' || dateFilter !== 'all') && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setSupplierFilter('all'); setDateFilter('all'); }}
                  className="h-9 text-slate-500"
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Materials List */}
        <div className="space-y-4" data-testid="materials-list">
          {filteredMaterials.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-lg text-center py-12">
              <Package className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500">
                {materials.length === 0 ? 'No raw materials recorded yet' : 'No materials match your search'}
              </p>
              {canEdit && materials.length === 0 && (
                <Button
                  onClick={() => setShowAddModal(true)}
                  className="mt-4"
                  variant="outline"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Entry
                </Button>
              )}
            </div>
          ) : viewMode === 'grouped' ? (
            /* Grouped View - Sorted by date (most recent first) */
            sortedGroupedMaterials.map(([invoiceNum, group]) => (
              <div key={invoiceNum} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                {/* Invoice Header */}
                <div 
                  className="flex items-center justify-between p-4 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => toggleInvoice(invoiceNum)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">Invoice #{invoiceNum}</p>
                      <p className="text-sm text-slate-500">
                        {group.supplier} • {formatDate(group.received_date)} • {group.items.length} items
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-600 hidden sm:block">
                      {group.items.reduce((sum, item) => sum + (item.quantity || 0), 0).toFixed(1)} total qty
                    </span>
                    {invoiceNum !== 'No Invoice' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleDownloadInvoice(invoiceNum); }}
                        className="h-8 w-8 p-0 text-green-500 hover:text-green-600 hover:bg-green-50"
                        title="Download Invoice"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                    )}
                    {canEdit && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); setConfirmDelete(invoiceNum); }}
                        className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                    {expandedInvoices.has(invoiceNum) ? (
                      <ChevronUp className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                </div>

                {/* Delete Confirmation */}
                {confirmDelete === invoiceNum && (
                  <div className="border-t border-red-200 bg-red-50 p-4">
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                      <div className="flex-1">
                        <p className="font-medium text-red-800">
                          {invoiceNum === 'No Invoice' ? 'Delete all items without invoice?' : 'Delete entire invoice?'}
                        </p>
                        <p className="text-sm text-red-600">
                          This will delete all {group.items.length} items {invoiceNum !== 'No Invoice' && `from invoice ${invoiceNum}`}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); setConfirmDelete(null); }}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handleDeleteInvoice(invoiceNum); }}
                          disabled={deletingInvoice === invoiceNum}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          {deletingInvoice === invoiceNum ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            'Delete All'
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Items */}
                {expandedInvoices.has(invoiceNum) && (
                  <div className="border-t border-slate-200">
                    <div className="hidden md:block">
                      <table className="w-full">
                        <thead>
                          <tr className="text-left text-xs font-medium text-slate-500 uppercase bg-slate-50">
                            <th className="px-4 py-3">Material</th>
                            <th className="px-4 py-3">Quantity</th>
                            <th className="px-4 py-3">Added By</th>
                            {canEdit && <th className="px-4 py-3 w-20">Actions</th>}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {group.items.map((mat) => (
                            <tr key={mat.material_id} className="hover:bg-slate-50">
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <Package className="w-4 h-4 text-slate-400" />
                                  <span className="font-medium text-slate-900">{mat.material_name}</span>
                                </div>
                                {mat.notes && <p className="text-xs text-slate-500 mt-1">{mat.notes}</p>}
                              </td>
                              <td className="px-4 py-3">
                                <span className="font-semibold text-slate-900">{mat.quantity}</span>
                                <span className="text-slate-500 ml-1">{mat.unit}</span>
                              </td>
                              <td className="px-4 py-3 text-slate-600 text-sm">{mat.created_by_name}</td>
                              {canEdit && (
                                <td className="px-4 py-3">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDelete(mat.material_id)}
                                    disabled={deletingId === mat.material_id}
                                    className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {/* Mobile view */}
                    <div className="md:hidden p-3 space-y-2">
                      {group.items.map((mat) => (
                        <div key={mat.material_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <Package className="w-5 h-5 text-green-500" />
                            <div>
                              <p className="font-medium text-slate-900">{mat.material_name}</p>
                              <p className="text-sm text-slate-500">{mat.quantity} {mat.unit}</p>
                            </div>
                          </div>
                          {canEdit && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(mat.material_id)}
                              disabled={deletingId === mat.material_id}
                              className="h-8 w-8 p-0 text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))
          ) : (
            /* List View */
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="table-header-row">
                      <th className="table-header-cell">Material</th>
                      <th className="table-header-cell">Quantity</th>
                      <th className="table-header-cell">Supplier</th>
                      <th className="table-header-cell">Batch No.</th>
                      <th className="table-header-cell">Received</th>
                      <th className="table-header-cell">Added By</th>
                      {canEdit && <th className="table-header-cell">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMaterials.map((mat) => (
                      <tr key={mat.material_id} className="table-row" data-testid={`material-row-${mat.material_id}`}>
                        <td className="table-cell">
                          <div className="flex items-center gap-2">
                            <Package className="w-4 h-4 text-slate-400" />
                            <span className="font-medium text-slate-900">{mat.material_name}</span>
                          </div>
                          {mat.notes && (
                            <p className="text-xs text-slate-500 mt-1">{mat.notes}</p>
                          )}
                        </td>
                        <td className="table-cell">
                          <span className="font-semibold text-slate-900">{mat.quantity}</span>
                          <span className="text-slate-500 ml-1">{mat.unit}</span>
                        </td>
                        <td className="table-cell text-slate-600">{mat.supplier || '-'}</td>
                        <td className="table-cell">
                          {mat.batch_number ? (
                            <span className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">{mat.batch_number}</span>
                          ) : '-'}
                        </td>
                        <td className="table-cell text-slate-600">{formatDate(mat.received_date)}</td>
                        <td className="table-cell text-slate-600 text-sm">{mat.created_by_name}</td>
                        {canEdit && (
                          <td className="table-cell">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(mat.material_id)}
                              disabled={deletingId === mat.material_id}
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>

              {/* Mobile Cards */}
              <div className="md:hidden p-4 space-y-3">
                {filteredMaterials.map((mat) => (
                  <div
                    key={mat.material_id}
                    className="bg-white border border-slate-200 rounded-lg p-4"
                    data-testid={`material-card-${mat.material_id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                          <Package className="w-5 h-5 text-slate-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">{mat.material_name}</p>
                          <p className="text-sm text-slate-500">{mat.quantity} {mat.unit}</p>
                        </div>
                      </div>
                      {canEdit && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(mat.material_id)}
                          disabled={deletingId === mat.material_id}
                          className="h-8 w-8 p-0 text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <p className="text-slate-500">Supplier</p>
                        <p className="font-medium">{mat.supplier || '-'}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Batch No.</p>
                        <p className="font-medium">{mat.batch_number || '-'}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Received</p>
                        <p className="font-medium">{formatDate(mat.received_date)}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Added By</p>
                        <p className="font-medium">{mat.created_by_name}</p>
                      </div>
                    </div>
                    {mat.notes && (
                      <p className="text-xs text-slate-500 mt-2 border-t pt-2">{mat.notes}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Add Material Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden" data-testid="add-material-modal">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Package className="w-5 h-5 text-green-600" />
                  </div>
                  <h2 className="text-lg font-bold text-slate-900">Add Raw Material</h2>
                </div>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto max-h-[60vh]">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Material Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    name="material_name"
                    value={formData.material_name}
                    onChange={handleInputChange}
                    placeholder="e.g., Titanium Dioxide"
                    className="h-12"
                    required
                    data-testid="material-name-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Quantity <span className="text-red-500">*</span>
                    </label>
                    <Input
                      name="quantity"
                      type="number"
                      step="0.01"
                      value={formData.quantity}
                      onChange={handleInputChange}
                      placeholder="0.00"
                      className="h-12"
                      required
                      data-testid="quantity-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Unit</label>
                    <Select value={formData.unit} onValueChange={(v) => setFormData(p => ({ ...p, unit: v }))}>
                      <SelectTrigger className="h-12" data-testid="unit-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="kg">Kilograms (kg)</SelectItem>
                        <SelectItem value="g">Grams (g)</SelectItem>
                        <SelectItem value="l">Liters (L)</SelectItem>
                        <SelectItem value="ml">Milliliters (ml)</SelectItem>
                        <SelectItem value="units">Units</SelectItem>
                        <SelectItem value="bags">Bags</SelectItem>
                        <SelectItem value="drums">Drums</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Supplier</label>
                  <Input
                    name="supplier"
                    value={formData.supplier}
                    onChange={handleInputChange}
                    placeholder="Supplier name"
                    className="h-12"
                    data-testid="supplier-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Batch Number</label>
                    <Input
                      name="batch_number"
                      value={formData.batch_number}
                      onChange={handleInputChange}
                      placeholder="Batch/Lot No."
                      className="h-12"
                      data-testid="batch-number-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Received Date <span className="text-red-500">*</span>
                    </label>
                    <Input
                      name="received_date"
                      type="date"
                      value={formData.received_date}
                      onChange={handleInputChange}
                      className="h-12"
                      required
                      data-testid="received-date-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="Any additional notes..."
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 resize-none"
                    rows={2}
                    data-testid="notes-input"
                  />
                </div>
              </form>

              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
                <Button variant="outline" onClick={() => setShowAddModal(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="bg-slate-900 hover:bg-slate-800"
                  data-testid="submit-material-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Entry
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
